/* =========================================================================
   Pure HTML builders for the list and detail views.
   No state mutation and no side effects — app.js owns those.
   ========================================================================= */

import { esc, fmtQty, scaledIngredients, heroAspect, imgAspect, CAT_EMOJI, CATEGORIES } from '../data/model.js'
import { blobUrl } from '../data/images.js'

export function cardHTML(r, selected) {
  const url = blobUrl(r.heroImage)
  const img = url ? `style="background-image:url('${url}');font-size:0"` : ''
  return `<div class="rcard" data-act="cardClick" data-id="${r.id}">
    <input type="checkbox" class="selbox" ${selected ? 'checked' : ''} data-act="toggleSel" data-id="${r.id}" aria-label="Select ${esc(r.title)}">
    <div class="thumb" ${img}>${url ? '' : (r.emoji || CAT_EMOJI[r.category] || '🍽️')}</div>
    <span class="catbadge">${esc(r.category)}</span>
    <button class="delcard" title="Delete recipe" aria-label="Delete ${esc(r.title)}" data-act="delRecipe" data-id="${r.id}">🗑</button>
    <div class="body">
      <h3>${esc(r.title)}</h3>
      <div class="meta">Serves ${r.serves} · ${r.ingredients.length} ingredients</div>
      <div class="tags">${(r.tags || []).map(t => '#' + esc(t)).join(' ')}</div>
    </div></div>`
}

export function listHTML(recipes, state, banner) {
  const cats = ['All', ...CATEGORIES.filter(c => recipes.some(r => r.category === c))]
  const q = state.q.trim().toLowerCase()

  const list = recipes.filter(r => {
    if (state.cat !== 'All' && r.category !== state.cat) return false
    if (!q) return true
    const hay = [
      r.title, r.category, (r.tags || []).join(' '),
      (r.source && r.source.name) || '',
      r.ingredients.map(i => i.item + ' ' + (i.unit || '')).join(' '),
    ].join(' ').toLowerCase()
    return q.split(/\s+/).every(w => hay.includes(w))
  })

  return `
    ${banner || ''}
    <div class="searchrow">
      <input id="q" type="search" enterkeyhint="search"
        placeholder="Search recipes, tags or ingredients — try “preserved lemon”"
        value="${esc(state.q)}" data-input-act="search" aria-label="Search recipes">
    </div>
    <div class="chips">${cats.map(c =>
      `<button class="chip ${state.cat === c ? 'on' : ''}" data-act="setCat" data-c="${esc(c)}">${esc(c)}</button>`
    ).join('')}</div>
    ${list.length
      ? `<div class="grid">${list.map(r => cardHTML(r, state.selected.has(r.id))).join('')}</div>`
      : `<div class="empty">${recipes.length
          ? 'No recipes match. Try a different search, or add a new recipe.'
          : 'No recipes yet. Tap ＋ Add recipe, or bring your collection in with ⇅ Share data → Import.'}</div>`}`
}

export function detailHTML(r, serves) {
  if (!r) return '<div class="empty">Recipe not found.</div>'

  const scaled = scaledIngredients(r, serves)
  const hUrl = blobUrl(r.heroImage)
  // the frame matches the crop aspect exactly, so nothing gets cut off twice
  const heroStyle = hUrl
    ? `style="background-image:url('${hUrl}');font-size:0;height:auto;aspect-ratio:${heroAspect(r)};max-height:70vh"`
    : ''

  return `
  <button class="back" data-act="showList">← All recipes</button>
  <div class="detail">
    <div class="hero" ${heroStyle}>${hUrl ? '' : (r.emoji || CAT_EMOJI[r.category] || '🍽️')}</div>
    <div class="dbody">
      <h2>${esc(r.title)}</h2>
      <div class="source">${esc((r.source && r.source.name) || '')}${
        r.source && r.source.url
          ? ` · <a href="${esc(r.source.url)}" target="_blank" rel="noopener">original link ↗</a>`
          : ''}</div>
      <div class="dtags"><span class="chip">${esc(r.category)}</span>${
        (r.tags || []).map(t => `<span class="chip">#${esc(t)}</span>`).join('')}</div>

      <div class="toolrow">
        <button class="btn small" data-act="shopSingle" data-id="${r.id}">🛒 Shopping list</button>
        <button class="btn small" data-act="openEdit" data-id="${r.id}">✎ Edit</button>
        <button class="btn small" data-act="addHero" data-id="${r.id}">🖼 ${hUrl ? 'Replace' : 'Set'} main photo</button>
        ${hUrl ? `<button class="btn small" data-act="editHero" data-id="${r.id}">✂️ Adjust main photo</button>` : ''}
        <button class="btn small" data-act="addGallery" data-id="${r.id}">＋ Add photo</button>
        <button class="btn small danger" data-act="delRecipe" data-id="${r.id}">Delete</button>
      </div>

      <div class="cols">
        <div class="panel">
          <h4>Ingredients</h4>
          <div class="serves">
            <button data-act="chgServes" data-id="${r.id}" data-d="-1" aria-label="Fewer serves">−</button>
            <span class="n">Serves ${serves}</span>
            <button data-act="chgServes" data-id="${r.id}" data-d="1" aria-label="More serves">＋</button>
          </div>
          <div class="origserves">${serves === r.serves
            ? 'Showing original recipe quantities.'
            : `Scaled from the original — <b>source of truth: serves ${r.serves}</b>. <a href="#" data-act="resetServes" data-id="${r.id}">Reset</a>`}</div>
          <ul class="ing">${scaled.map(i =>
            `<li><span class="qty">${esc(fmtQty(i.sq))}${i.sq != null && i.unit ? ' ' + esc(i.unit) : (i.unit ? esc(i.unit) : '')}</span><span>${esc(i.item)}</span></li>`
          ).join('')}</ul>
        </div>

        <div class="panel">
          <h4>Method</h4>
          <ol class="method">${r.method.map(s => `<li>${esc(s)}</li>`).join('')}</ol>

          <h4>Cook's notes — things we've learnt</h4>
          ${(r.notes || []).map(n => `<div class="notebox">${esc(n.text)}<div class="d">${esc(n.date || '')}</div></div>`).join('')
            || '<div class="hint">No notes yet.</div>'}
          <div class="addnote">
            <input id="noteIn" placeholder="Add a note for next time…" data-enter-act="addNote" data-id="${r.id}" enterkeyhint="done">
            <button class="btn small" data-act="addNote" data-id="${r.id}">Add</button>
          </div>

          <h4>Paired well with</h4>
          ${(r.pairings || []).map(p => `<div class="pairbox">${esc(p.text)}<div class="d">${esc(p.date || '')}</div></div>`).join('')
            || '<div class="hint">Nothing recorded yet.</div>'}
          <div class="addnote">
            <input id="pairIn" placeholder="e.g. Went beautifully with a pinot noir…" data-enter-act="addPairing" data-id="${r.id}" enterkeyhint="done">
            <button class="btn small" data-act="addPairing" data-id="${r.id}">Add</button>
          </div>

          ${(r.images && r.images.length) ? `<h4>More photos</h4><div class="gallery">${
            r.images.map((im, i) => {
              const u = blobUrl(im)
              return `<div class="gwrap">
                <div class="g" style="background-image:url('${u}');height:auto;aspect-ratio:${imgAspect(r, i)}"></div>
                <div class="gbtns">
                  <button class="btn small" title="Adjust" data-act="editGallery" data-id="${r.id}" data-i="${i}">✂️</button>
                  <button class="btn small" title="Remove" data-act="delGallery" data-id="${r.id}" data-i="${i}">🗑</button>
                </div></div>`
            }).join('')}</div>` : ''}
        </div>
      </div>
    </div>
  </div>`
}
